import { Modal } from 'antd';
import type { License } from '@/types/license';
import { readLicenseFromStorage } from '@/utils/licenseStorage';

/**
 * Route tương ứng menu V1 `_MenuPartial` khi `checkDemo` → chặn mở menu (alert).
 * (Đồng bộ với `alertDemo: true` trên Navbar V2.)
 */
export const DEMO_MENU_BLOCKED_PATH_PREFIXES: string[] = [
  '/role-approve',
  '/dot-on-tap',
  '/van-hoa/dot-on-tap',
  '/van-hoa/huong-dan',
  '/kpi-chung',
  '/kpi-ngoai-le',
  '/kpiipoo',
  '/kpi-theo-ipoo',
  '/thu-vien-kpi',
  '/hieu-suat-kpi',
  '/giai-doan',
  '/kpi-cong-thuc',
  '/cau-hinh-chung',
  '/thiet-lap/license',
  '/thiet-lap/don-vi-do-luong',
  '/van-hoa/thiet-lap/don-vi-do-luong',
  '/chuyen-du-lieu',
  '/thiet-lap/api-co-cau',
  '/van-hoa/thiet-lap/api-co-cau',
  '/thiet-lap/api-sync-data',
  '/van-hoa/thiet-lap/api-sync-data',
  '/thiet-lap/api-co-cau/thong-bao',
  '/van-hoa/thiet-lap/api-co-cau/thong-bao',
  '/database',
  '/logfiles',
  '/van-hoa/logfiles',
  '/huong-dan',
  '/thong-ke-bao-cao-kpis',
  '/tong-hop-ca-nhan',
  '/cau-hinh-email',
  '/van-hoa/cau-hinh-email',
  '/quy-trinh/cau-hinh-email',
];

/**
 * Route tương ứng page V1 có `Model.CheckDemo` / `ViewBag.CheckDemo`
 * → vào được trang nhưng không CRUD (click hiện alert).
 */
export const DEMO_CRUD_BLOCKED_PATH_PREFIXES: string[] = [
  ...DEMO_MENU_BLOCKED_PATH_PREFIXES,

  // Tổ chức (NhanVien, BoPhan, ChucVu, NhomQuyen, QuyenXetDuyet)
  '/to-chuc/nhan-vien',
  '/to-chuc/khoa-dang-nhap',
  '/to-chuc/bo-phan',
  '/to-chuc/chuc-vu',
  '/van-hoa/to-chuc/nhan-vien',
  '/van-hoa/to-chuc/khoa-dang-nhap',
  '/van-hoa/to-chuc/bo-phan',
  '/van-hoa/to-chuc/chuc-vu',
  '/role-group',
  '/van-hoa/role-group',
  '/van-hoa/role-approve',

  // Bảng kế hoạch (Home/IndexKeHoach)
  '/bang-ke-hoach',
  '/van-hoa/bang-ke-hoach',

  // Triển khai BSC — Buoc3→Buoc11, DuAn, CongViec
  '/ket-qua-chien-luoc',
  '/ban-do-chien-luoc',
  '/ban-do-chien-luoc-thanh-phan',
  '/buoc5',
  '/buoc6',
  '/buoc7',
  '/buoc7-danhsachthucong',
  '/buoc-7-ti-trong-thu-cong',
  '/du-an',
  '/van-hoa/du-an',
  '/action-plan',
  '/action-plan-v2',
  '/cong-viec-du-an',
  '/tong-quan-cong-viec',
  '/tong-quan-cong-viec-v2',

  // Báo cáo KPIs / nhanh / API (Buoc11, BaoCaoNhanhKPI, …)
  '/bao-cao-kpis',
  '/bao-cao-nhanh',
  '/bao-cao-bsc',
  '/bao-cao-chi-tiet-bsc',
  '/bao-cao-viec-trong-ngay',
  '/report-kpi-from-api',

  // Dashboard variants (Index có CheckDemo)
  '/van-hoa/dashboard',
  '/tong-quan',
  '/tong-quan-hieu-suat',
];

/** Dashboard root path — match exact (không dùng prefix `/`). */
export const DEMO_CRUD_EXACT_PATHS: string[] = ['/', '/van-hoa'];

const normalizePath = (path: string): string => {
  const raw = String(path || '').split('?')[0].split('#')[0].trim();
  if (!raw) return '/';
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  return withSlash.replace(/\/+$/, '') || '/';
};

const pathMatchesPrefixes = (pathname: string, prefixes: string[]): boolean => {
  const path = normalizePath(pathname);
  return prefixes.some((prefix) => {
    const p = normalizePath(prefix);
    return path === p || path.startsWith(`${p}/`);
  });
};

export const isLicenseDemo = (license?: License | null): boolean => {
  if (!license) return false;
  const raw = license as License & { isDemo?: boolean };
  return !!(raw.IsDemo ?? raw.isDemo);
};

/** Đọc license từ storage theo tenant hiện tại. */
export const resolveIsDemoTenant = (tenantID?: string | null): boolean => {
  if (!tenantID) return false;
  const license = readLicenseFromStorage(tenantID);
  return isLicenseDemo(license ?? null);
};

export const isDemoCrudBlockedPath = (pathname: string): boolean => {
  const path = normalizePath(pathname);
  if (DEMO_CRUD_EXACT_PATHS.includes(path)) return true;
  return pathMatchesPrefixes(path, DEMO_CRUD_BLOCKED_PATH_PREFIXES);
};

export const isDemoMenuBlockedPath = (pathname: string): boolean =>
  pathMatchesPrefixes(pathname, DEMO_MENU_BLOCKED_PATH_PREFIXES);

type TranslateFn = (key: string, defaultValue: string) => string;

export const showDemoAccountBlockedModal = (translate: TranslateFn) => {
  Modal.warning({
    title: translate('bsc.title.taikhoandemo', 'Tài khoản demo'),
    content: translate(
      'bsc.msg.chucnangkhongkhadungdemo',
      'Chức năng này không khả dụng trong tài khoản demo!\nVui lòng liên hệ với Sale phụ trách tài khoản của quý khách nếu có nhu cầu nâng cấp lên tài khoản chính thức.',
    ),
    okText: translate('bsc.btn.dahieu', 'Đã hiểu'),
    centered: true,
  });
};
